import { DemeterCommodity, DemeterInsurancePositionModel, RunCalculationEngineResponse } from '../../../../../Generated/Raven-Demeter';

const commodityToFuturesVariableNameMap: Record<string, string> = {
    [DemeterCommodity.MilkClass3]: 'class3MilkFuturesPrice',
    [DemeterCommodity.MilkClass4]: 'class4MilkFuturesPrice',
};

const commodityToInsuranceVariableNameMap: Record<string, string> = {
    [DemeterCommodity.MilkClass3]: 'insuranceClass3Price',
    [DemeterCommodity.MilkClass4]: 'insuranceClass4Price',
};

const livestockVariableNames = {
    weightClass1Price: 'insuranceFeederCattleWeightClass1Price',
    weightClass1Volume: 'insuranceFeederCattleWeightClass1Volume',
    weightClass2Price: 'insuranceFeederCattleWeightClass2Price',
    weightClass2Volume: 'insuranceFeederCattleWeightClass2Volume',
} as const;

export type DRPPositionGroup = {
    periodKey: string;
    periodDate: Date;
    positions: DemeterInsurancePositionModel[];
    productionQuantity: number;
    endorsedQuantity: number;
    protectedQuantity: number;
    endorsedPercent: number;
    protectedPercent: number;
};

export type LRPPositionGroup = {
    periodKey: string;
    periodDate: Date;
    positions: DemeterInsurancePositionModel[];
    headInsured: number;
    marketPrice: number | null;
    coveragePrice: number | null;
    adjustedCoveragePrice: number | null;
};

export type ClassificationData = {
    subCommodity: string;
    positions: DemeterInsurancePositionModel[];
};
/* eslint-disable class-methods-use-this */
class HedgeMapChartsService {
    // Shared - Retrieves a calculator field value by date and variable name
    getCalculatorFieldValue = (results: RunCalculationEngineResponse['results'], asOfDate: string, fieldName: string): number | null => {
        const lines = results?.find((result) => new Date(result.asOfDate).getTime() >= new Date(asOfDate).getTime())?.lines;
        return lines?.find((line) => line.variableName === fieldName)?.value ?? null;
    };

    // Shared - Maps an array of models to chart series data points using a single variable name
    mapCompositeModelsToSeriesData = (
        results: RunCalculationEngineResponse['results'],
        models: Array<{ asOfDate: string }>,
        variableName: string,
    ): Array<{ value: number; asOfDate: Date; isActualValue: true }> =>
        models.map((model) => ({
            value: this.getCalculatorFieldValue(results, model.asOfDate, variableName) ?? 0,
            asOfDate: new Date(model.asOfDate),
            isActualValue: true as const,
        }));

    // DRP Methods
    buildDRPGroupedPositions = (filteredPositions: DemeterInsurancePositionModel[], results: RunCalculationEngineResponse['results']): DRPPositionGroup[] => {
        if (!filteredPositions?.length || !results) {
            return [];
        }

        const periodMap = this.groupPositionsByPeriod(filteredPositions);
        return Array.from(periodMap.entries())
            .map(([periodKey, group]) => this.createDRPPositionGroup(periodKey, group.periodDate, group.positions, results))
            .sort((a, b) => a.periodDate.getTime() - b.periodDate.getTime());
    };

    getUniqueCommoditiesFromPositions = (positions: DemeterInsurancePositionModel[]) => {
        const commodities = new Set(positions.map((position) => position.commodity));
        return Array.from(commodities).sort();
    };

    getFuturesVariableNameForCommodity = (commodity: string): string | null => commodityToFuturesVariableNameMap[commodity] ?? null;

    getInsuranceVariableNameForCommodity = (commodity: string): string | null => commodityToInsuranceVariableNameMap[commodity] ?? null;

    getDRPQuarterMidpointDate = (quarterStartDate: Date): Date => new Date(quarterStartDate.getFullYear(), quarterStartDate.getMonth() + 1, 1);

    formatDRPInsuranceQuarterLabel = (periodKey: string): string => {
        const parts = periodKey.split('_');
        if (parts.length === 2 && parts[1].startsWith('Q')) {
            return `${parts[1]} ${parts[0]}`;
        }

        return periodKey;
    };

    private groupPositionsByPeriodKey = (
        positions: DemeterInsurancePositionModel[],
        keyExtractor: (position: DemeterInsurancePositionModel) => string,
        dateExtractor: (position: DemeterInsurancePositionModel) => Date,
    ): Map<string, { periodDate: Date; positions: DemeterInsurancePositionModel[] }> => {
        const periodMap = new Map<string, { periodDate: Date; positions: DemeterInsurancePositionModel[] }>();

        positions.forEach((position) => {
            const periodKey = keyExtractor(position);
            const periodDate = dateExtractor(position);

            if (!periodMap.has(periodKey)) {
                periodMap.set(periodKey, { periodDate, positions: [] });
            }

            periodMap.get(periodKey)!.positions.push(position);
        });

        return periodMap;
    };

    private getDRPCoverageDateForPosition = (position: DemeterInsurancePositionModel): Date => {
        if (position.coverageQuarter) {
            return new Date(position.coverageStartDate);
        }
        return position.coveragePeriodDate ? new Date(position.coveragePeriodDate) : new Date(position.coverageStartDate);
    };

    private groupPositionsByPeriod = (positions: DemeterInsurancePositionModel[]) =>
        this.groupPositionsByPeriodKey(
            positions,
            (position) => this.getDRPPeriodKeyForPosition(position),
            (position) => this.getDRPQuarterDate(this.getDRPCoverageDateForPosition(position)),
        );

    private createDRPPositionGroup = (
        periodKey: string,
        periodDate: Date,
        positions: DemeterInsurancePositionModel[],
        results: RunCalculationEngineResponse['results'],
    ): DRPPositionGroup => {
        const endorsedQuantity = this.calculateDRPEndorsedQuantity(positions);
        const protectedQuantity = this.calculateDRPProtectedQuantity(positions);
        const productionQuantity = this.getDRPProductionQuantityForPeriod(positions, results);
        const endorsedPercent = endorsedQuantity / productionQuantity;
        const protectedPercent = protectedQuantity / productionQuantity;
        return {
            periodKey,
            periodDate,
            positions,
            productionQuantity,
            endorsedQuantity,
            protectedQuantity,
            endorsedPercent,
            protectedPercent,
        };
    };

    private getDRPPeriodKeyForPosition = (position: DemeterInsurancePositionModel): string => {
        if (position.coverageQuarter) {
            return position.coverageQuarter;
        }

        const coverageDate = position.coveragePeriodDate ? new Date(position.coveragePeriodDate) : new Date(position.coverageStartDate);
        return this.getDRPQuarterKeyFromDate(coverageDate);
    };

    private getDRPQuarterKeyFromDate = (date: Date) => {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `${date.getFullYear()}_Q${quarter}`;
    };

    private getDRPQuarterDate = (date: Date) => {
        const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
        return new Date(date.getFullYear(), quarterStartMonth, 1);
    };

    private calculateDRPEndorsedQuantity = (positions: DemeterInsurancePositionModel[]) =>
        positions.reduce((sum, position) => {
            const coverageLevel = position.coverageLevel ?? 0;
            const normalizedCoverageLevel = coverageLevel > 1 ? coverageLevel / 100 : coverageLevel;
            const declared = position.declaredProduction ?? 0;
            return sum + declared * normalizedCoverageLevel;
        }, 0);

    private calculateDRPProtectedQuantity = (positions: DemeterInsurancePositionModel[]) =>
        positions.reduce((sum, position) => {
            if (position.protectedProduction !== null && position.protectedProduction !== undefined) {
                return sum + position.protectedProduction;
            }

            const coverageLevel = position.coverageLevel ?? 0;
            const normalizedCoverageLevel = coverageLevel > 1 ? coverageLevel / 100 : coverageLevel;
            const declared = position.declaredProduction ?? position.declaredVolume ?? position.head ?? 0;
            const protectionFactor = position.protectionFactor ?? 1;
            return sum + declared * normalizedCoverageLevel * protectionFactor;
        }, 0);

    private getDRPProductionQuantityForPeriod = (positions: DemeterInsurancePositionModel[], results: RunCalculationEngineResponse['results']) => {
        if (!positions.length || !results) {
            return 0;
        }

        const startDates = positions.map((p) => new Date(p.coverageStartDate)).filter((d) => !Number.isNaN(d.getTime()));
        const endDates = positions.map((p) => new Date(p.coverageEndDate)).filter((d) => !Number.isNaN(d.getTime()));

        if (startDates.length === 0 || endDates.length === 0) {
            return 0;
        }

        const rangeStart = new Date(Math.min(...startDates.map((d) => d.getTime())));
        const rangeEnd = new Date(Math.max(...endDates.map((d) => d.getTime())));

        const productionSum = results.reduce((sum, result) => {
            const resultDate = new Date(result.asOfDate);

            if (resultDate >= rangeStart && resultDate <= rangeEnd) {
                const productionLine = result.lines.find((line) => line.variableName === 'production');
                return sum + (productionLine?.value ?? 0);
            }

            return sum;
        }, 0);

        return productionSum;
    };

    // LRP - Methods
    calculateLRPWeightedAverageInsurancePrice = (results: RunCalculationEngineResponse['results'], asOfDate: string): number | null => {
        const weightClass1Price = this.getCalculatorFieldValue(results, asOfDate, livestockVariableNames.weightClass1Price);
        const weightClass1Volume = this.getCalculatorFieldValue(results, asOfDate, livestockVariableNames.weightClass1Volume);
        const weightClass2Price = this.getCalculatorFieldValue(results, asOfDate, livestockVariableNames.weightClass2Price);
        const weightClass2Volume = this.getCalculatorFieldValue(results, asOfDate, livestockVariableNames.weightClass2Volume);

        const totalVolume = (weightClass1Volume ?? 0) + (weightClass2Volume ?? 0);

        if (totalVolume === 0) {
            return null;
        }

        return ((weightClass2Price ?? 0) * (weightClass2Volume ?? 0) + (weightClass1Price ?? 0) * (weightClass1Volume ?? 0)) / totalVolume;
    };

    groupPositionsBySubCommodity = (positions: DemeterInsurancePositionModel[]): ClassificationData[] => {
        if (!positions?.length) {
            return [];
        }

        const classificationMap = new Map<string, DemeterInsurancePositionModel[]>();

        positions.forEach((position) => {
            const classification = position.subCommodity || '';

            if (!classificationMap.has(classification)) {
                classificationMap.set(classification, []);
            }

            classificationMap.get(classification)!.push(position);
        });

        return Array.from(classificationMap.entries()).map(([subCommodity, insurancePositions]) => ({
            subCommodity,
            positions: insurancePositions,
        }));
    };

    buildLRPGroupedPositions = (positions: DemeterInsurancePositionModel[]): LRPPositionGroup[] => {
        const periodMap = this.groupLRPPositionsByMonth(positions);
        return Array.from(periodMap.entries())
            .map(([periodKey, group]) => this.createLRPPositionGroup(periodKey, group.periodDate, group.positions))
            .sort((a, b) => a.periodDate.getTime() - b.periodDate.getTime());
    };

    private groupLRPPositionsByMonth = (positions: DemeterInsurancePositionModel[]) =>
        this.groupPositionsByPeriodKey(
            positions,
            (position) => {
                const coverageDate = position.coveragePeriodDate ? new Date(position.coveragePeriodDate) : new Date(position.coverageStartDate);
                const monthDate = new Date(coverageDate.getFullYear(), coverageDate.getMonth(), 1);
                return `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
            },
            (position) => {
                const coverageDate = position.coveragePeriodDate ? new Date(position.coveragePeriodDate) : new Date(position.coverageStartDate);
                return new Date(coverageDate.getFullYear(), coverageDate.getMonth(), 1);
            },
        );

    private createLRPPositionGroup = (periodKey: string, periodDate: Date, positions: DemeterInsurancePositionModel[]): LRPPositionGroup => {
        const headInsured = this.calculateLRPTotalHeadInsured(positions);
        const marketPrice = this.calculateLRPWeightedAverage(positions, headInsured, (position) => position.currentMarketPrice ?? 0);
        const coveragePrice = this.calculateLRPWeightedAverage(positions, headInsured, (position) => position.coveragePrice ?? 0);
        const adjustedCoveragePrice = this.calculateLRPWeightedAverage(
            positions,
            headInsured,
            (position) => (position.coveragePrice ?? 0) * ((position.weightClassAdjustmentPercent ?? 100) / 100),
        );

        return {
            periodKey,
            periodDate,
            positions,
            headInsured,
            marketPrice,
            coveragePrice,
            adjustedCoveragePrice,
        };
    };

    private calculateLRPTotalHeadInsured = (positions: DemeterInsurancePositionModel[]): number =>
        positions.reduce((sum, position) => sum + (position.head ?? 0), 0);

    private calculateLRPWeightedAverage = (
        positions: DemeterInsurancePositionModel[],
        totalHeads: number,
        priceExtractor: (position: DemeterInsurancePositionModel) => number,
    ): number | null => {
        if (totalHeads === 0) {
            return null;
        }

        const weightedSum = positions.reduce((sum, position) => {
            const price = priceExtractor(position);
            const heads = position.head ?? 0;
            return sum + price * heads;
        }, 0);

        return weightedSum / totalHeads;
    };
}

const hedgeMapChartsService = new HedgeMapChartsService();

export default hedgeMapChartsService;
